import { useTable } from '@refinedev/antd';
import { useCustomMutation } from '@refinedev/core';
import { Table, Tag, Dropdown, Button, Modal, Select, Input, message } from 'antd';
import { MoreVertical, Plus, FileText } from 'lucide-react';
import { useState, type FC } from 'react';
import InvoiceDrawer from '@/components/ops/InvoiceDrawer';

interface SubscriptionRecord {
  id: string;
  organizationId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string | null;
  planType: string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAt: string | null;
  createdAt: string;
}

const PLAN_TAG: Record<string, { color: string; text: string }> = {
  free: { color: 'default', text: 'Free' }, pro: { color: 'blue', text: 'Pro' },
  team: { color: 'purple', text: 'Team' }, business: { color: 'gold', text: 'Business' },
  enterprise: { color: 'green', text: 'Enterprise' },
};
const STATUS_TAG: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: 'Active' }, past_due: { color: 'warning', text: 'Past Due' },
  canceled: { color: 'default', text: 'Annulé' }, incomplete: { color: 'error', text: 'Incomplet' },
  free: { color: 'default', text: 'Free' },
};
const PLAN_OPTIONS = [{ value: 'pro', label: 'Pro' }, { value: 'team', label: 'Team' }, { value: 'business', label: 'Business' }];
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const SubscriptionsTable: FC<{ onMutationSuccess?: () => void }> = ({ onMutationSuccess }) => {
  const { tableProps } = useTable<SubscriptionRecord>({ resource: 'admin/billing/subscriptions', pagination: { pageSize: 15 } });
  const { mutate: changePlan, isLoading: changingPlan } = useCustomMutation();
  const { mutate: cancelSub, isLoading: canceling } = useCustomMutation();
  const { mutate: createSub, isLoading: creating } = useCustomMutation();

  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [selected, setSelected] = useState<SubscriptionRecord | null>(null);
  const [newPlan, setNewPlan] = useState('pro');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ organizationId: '', email: '', organizationName: '', planType: 'pro' });
  const [invoiceSubId, setInvoiceSubId] = useState<string | null>(null);

  const success = () => onMutationSuccess?.();
  const handleChangePlan = () => {
    if (!selected) return;
    changePlan(
      { url: `/api/v1/admin/billing/subscriptions/${selected.organizationId}/plan`, method: 'put', values: { newPlanType: newPlan } },
      { onSuccess: () => { setChangePlanOpen(false); message.success('Plan modifié'); success(); } },
    );
  };
  const handleCancel = (r: SubscriptionRecord) =>
    Modal.confirm({
      title: 'Annuler l\'abonnement ?', content: `Organisation ${r.organizationId.slice(0, 8)}… sera annulée en fin de période.`,
      okText: 'Confirmer', okButtonProps: { danger: true, loading: canceling }, cancelText: 'Retour',
      onOk: () => new Promise<void>((resolve) => cancelSub(
        { url: `/api/v1/admin/billing/subscriptions/${r.organizationId}/cancel`, method: 'post', values: { immediately: false } },
        { onSuccess: () => { message.success('Abonnement annulé'); success(); resolve(); } },
      )),
    });
  const handleCreate = () => createSub(
    { url: '/api/v1/admin/billing/subscriptions', method: 'post', values: form },
    { onSuccess: () => { setCreateOpen(false); message.success('Abonnement créé'); success(); } },
  );

  return (
    <div className="fc-card overflow-hidden p-0">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Abonnements</h3>
        <Button type="primary" size="small" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setCreateOpen(true)}>Créer</Button>
      </div>
      <Table {...tableProps} rowKey="id" size="middle" scroll={{ x: 800 }}>
        <Table.Column dataIndex="organizationId" title="Organisation" ellipsis render={(v: string) => <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{v?.slice(0, 8)}…</span>} />
        <Table.Column dataIndex="planType" title="Plan" width={100} render={(p: string) => { const c = PLAN_TAG[p] ?? { color: 'default', text: p }; return <Tag color={c.color}>{c.text}</Tag>; }} />
        <Table.Column dataIndex="status" title="Statut" width={110} render={(s: string) => { const c = STATUS_TAG[s] ?? { color: 'default', text: s }; return <Tag color={c.color}>{c.text}</Tag>; }} />
        <Table.Column dataIndex="currentPeriodEnd" title="Fin période" width={120} render={(d: string | null) => <span className="text-xs text-slate-500 dark:text-slate-400">{fmtDate(d)}</span>} />
        <Table.Column title="Actions" width={80} fixed="right" render={(_: unknown, r: SubscriptionRecord) => (
          <Dropdown menu={{ items: [
            { key: 'change', label: 'Changer de plan', onClick: () => { setSelected(r); setNewPlan(r.planType); setChangePlanOpen(true); } },
            { key: 'cancel', label: 'Annuler', danger: true, onClick: () => handleCancel(r) },
            { type: 'divider' },
            { key: 'invoices', label: 'Factures', icon: <FileText className="h-3.5 w-3.5" />, onClick: () => setInvoiceSubId(r.id) },
          ] }} trigger={['click']}><Button type="text" size="small" icon={<MoreVertical className="h-4 w-4" />} /></Dropdown>
        )} />
      </Table>

      <Modal title="Changer de plan" open={changePlanOpen} onCancel={() => setChangePlanOpen(false)} onOk={handleChangePlan} confirmLoading={changingPlan} okText="Appliquer" cancelText="Annuler">
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">Organisation : <strong>{selected?.organizationId?.slice(0, 8)}…</strong></p>
        <Select className="w-full" value={newPlan} onChange={setNewPlan} options={PLAN_OPTIONS} />
      </Modal>

      <Modal title="Créer un abonnement" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={handleCreate} confirmLoading={creating} okText="Créer" cancelText="Annuler">
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">Organization ID</label><Input value={form.organizationId} onChange={(e) => setForm((f) => ({ ...f, organizationId: e.target.value }))} placeholder="UUID" /></div>
          <div><label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">Email</label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="admin@org.com" /></div>
          <div><label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">Nom organisation</label><Input value={form.organizationName} onChange={(e) => setForm((f) => ({ ...f, organizationName: e.target.value }))} /></div>
          <div><label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">Plan</label><Select className="w-full" value={form.planType} onChange={(v) => setForm((f) => ({ ...f, planType: v }))} options={PLAN_OPTIONS} /></div>
        </div>
      </Modal>

      <InvoiceDrawer subscriptionId={invoiceSubId} onClose={() => setInvoiceSubId(null)} />
    </div>
  );
};

export default SubscriptionsTable;
